import { TaskDefinition, TaskDefinitionStatus, TaskExecution, TaskExecutionStatus } from "@core/models/domain/restriction.domain";
import { TaskDefinitionResponse, TaskExecutionResponse } from "@core/models/response/restriction.response";
import { getRepeatSelectedDays } from "@core/utils/service";

/**
 * Maps backend task execution status strings to the TaskExecutionStatus enum.
 * 
 * @param {string} status - The status string from the backend
 * @returns {TaskExecutionStatus} The corresponding TaskExecutionStatus enum value
 */
function mapTaskExecutionStatus(status: string): TaskExecutionStatus {
  const statusMap: { [key: string]: TaskExecutionStatus } = {
    'NOT_STARTED': TaskExecutionStatus.NOT_STARTED,
    'PENDING': TaskExecutionStatus.NOT_STARTED,
    'IN_PROGRESS': TaskExecutionStatus.IN_PROGRESS,
    'COMPLETED': TaskExecutionStatus.COMPLETED,
    'APPROVED': TaskExecutionStatus.COMPLETED, // Backend approved status maps to completed
    'RESTRICTION_CANCELLED': TaskExecutionStatus.RESTRICTION_CANCELLED,
    'CANCELLED': TaskExecutionStatus.CANCELLED,
    'SKIPPED': TaskExecutionStatus.SKIPPED,
  };
  
  return statusMap[status] || TaskExecutionStatus.NOT_STARTED;
}

/**
 * Converts a `TaskDefinitionResponse` object into a `TaskDefinition` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts date fields such as `startAt`, `endAt`, `cancelledTaskDates`, `creationDatetime`, `validityStartDate`, and `validityEndDate` into JavaScript `Date` objects.
 * Additionally, it processes the `repeatSelectedDays` field using the `getRepeatSelectedDays` utility function.
 *
 * @param {TaskDefinitionResponse} response - The response object containing task definition information.
 * @returns {TaskDefinition} A domain object representing the task definition.
 */
export function buildTaskDefinitionFromResponse(response: TaskDefinitionResponse): TaskDefinition {
  return {
    id: response.taskDefinitionId,
    children: response.children,
    title: response.title,
    description: response.description,
    schoolInfo: buildTaskSchoolInfoFromResponse(response.taskSchoolInfo),
    status: response.status as TaskDefinitionStatus,
    repeatSelectedDays: getRepeatSelectedDays(response.repeatSelectedDays),
    startAt: response.startAt ? new Date(response.startAt) : undefined,
    endAt: response.endAt ? new Date(response.endAt) : undefined,
    cancelledTaskDates: response.cancelledTaskDates ? response.cancelledTaskDates.map((date: string) => new Date(date)) : [],
    creationDatetime: response.creationDatetime ? new Date(response.creationDatetime) : undefined,
    validityStartDate: response.validityStartDate ? new Date(response.validityStartDate) : undefined,
    validityEndDate: response.validityEndDate ? new Date(response.validityEndDate) : undefined,
    timezone: response.timezone,
    createdBy: response.createdBy,
    followedBy: response.followedBy,
    // Passed through as the backend sent it: dates and times stay strings so no timezone
    // conversion can shift a suspension onto the wrong day in a browser east or west of the school.
    suppressedPeriods: response.suppressedPeriods ?? []
  };
}

/**
 * Converts a `TaskExecutionResponse` object into a `TaskExecution` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts date fields such as `scheduledAt`, `scheduledEndTime`, `startedAt`, `endedAt`, `creationDatetime`, and `lastUpdateDatetime` into JavaScript `Date` objects.
 *
 * @param {TaskExecutionResponse} response - The response object containing task execution information.
 * @returns {TaskExecution} A domain object representing the task execution.
 */
export function buildTaskExecutionFromResponse(response: TaskExecutionResponse): TaskExecution {
  return {
    id: response.taskExecutionId || response.id, // Support both field names
    taskDefinitionId: response.taskDefinitionId,
    status: mapTaskExecutionStatus(response.status),
    comment: response.comment,
    scheduledAt: response.scheduledAt ? new Date(response.scheduledAt) : undefined,
    scheduledEndTime: response.scheduledEndTime ? new Date(response.scheduledEndTime) : undefined,
    startedAt: response.startedAt ? new Date(response.startedAt) : undefined,
    endedAt: response.endedAt ? new Date(response.endedAt) : undefined,
    creationDatetime: response.creationDatetime ? new Date(response.creationDatetime) : undefined,
    lastUpdateDatetime: response.lastUpdateDatetime ? new Date(response.lastUpdateDatetime) : undefined,
  };
}

/**
 * Converts a task school information object into a simplified domain object.
 *
 * This function maps the properties of the task school information object to the corresponding fields in the domain object.
 *
 * @param {any} taskSchoolInfo - The object containing task school information.
 * @returns {any} A simplified domain object representing the task school information.
 */
export function buildTaskSchoolInfoFromResponse(taskSchoolInfo: any): any {
  return {
    schoolAccountId: taskSchoolInfo.schoolAccountId,
    assignedGroups: taskSchoolInfo.assignedGroups || taskSchoolInfo.groupIds || []
  };
}